import { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Crown, ShieldCheck } from "lucide-react";
import type { PresencePayload, PresenceViewer } from "@wedding/shared";
import { cn } from "./lib/cn";
import { AvatarBubble } from "./avatar-bubble";
import { AudienceSheet, orderViewers } from "./audience-sheet";

const MAX_STACK = 3;

/** One facepile avatar; the director gets a subtle golden ring + small crown. */
function StackAvatar({ viewer }: { viewer: PresenceViewer }) {
  const king = viewer.role === "reji";
  const admin = viewer.role === "admin";
  return (
    <span className="relative" data-king={king || undefined} data-admin={admin || undefined}>
      <AvatarBubble
        name={viewer.name}
        className={cn(
          "size-7 text-[11px] ring-2",
          // Quiet accent ring: gold for the director, sky for a platform admin,
          // soft white for guests — no loud disc so it doesn't dominate.
          king ? "ring-amber-300/70" : admin ? "ring-sky-300/70" : "ring-white/15",
        )}
      />
      {king && (
        <span className="absolute -top-1 -right-1 flex items-center justify-center size-3 rounded-full bg-amber-300/90 text-black ring-1 ring-black/25">
          <Crown className="size-2" strokeWidth={2.5} />
        </span>
      )}
      {admin && (
        <span className="absolute -top-1 -right-1 flex items-center justify-center size-3 rounded-full bg-sky-300/90 text-black ring-1 ring-black/25">
          <ShieldCheck className="size-2" strokeWidth={2.5} />
        </span>
      )}
    </span>
  );
}

export interface PresenceFacepileProps {
  presence: PresencePayload | null;
  /** Override the tap behaviour; when omitted the pile owns its AudienceSheet. */
  onTap?: () => void;
  /** Portal stacking for the owned sheet (default z-[120]; Reji uses z-[70]). */
  sheetZClassName?: string;
  className?: string;
}

/**
 * Live audience for the Sahne — market pattern (IG/TikTok Live): a facepile of
 * size-7 avatars led by the crowned director (that IS the "Reji is live" signal,
 * no separate badge), trailed by an avatar-shaped "+N" overflow chip that opens
 * the audience sheet. The whole pill is one button; tap → `onTap` or the owned
 * bottom sheet.
 */
function PresenceFacepileImpl({
  presence,
  onTap,
  sheetZClassName = "z-[120]",
  className,
}: PresenceFacepileProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const ordered = useMemo(() => (presence ? orderViewers(presence) : []), [presence]);

  // Nothing to show only when literally no one is watching (no guests, no
  // director) — a lone crowned director now shows even at total === 0.
  if (!presence || ordered.length === 0) return null;

  const shown = ordered.slice(0, MAX_STACK);
  // presence.total counts EVERYONE watching (guests + the director/admin on the
  // Reji sockets), so the overflow is simply the people not pictured here.
  // `99+` past the two-digit cap keeps the chip circular.
  const overflow = Math.max(0, presence.total - shown.length);
  const overflowLabel = overflow > 99 ? "99+" : `+${overflow}`;

  return (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (onTap) onTap();
          else setOpen(true);
        }}
        aria-label={`${t("slideshow.presenceTitle")} · ${presence.total}`}
        className={cn(
          "pointer-events-auto flex items-center rounded-full bg-white/10 backdrop-blur-md border border-white/15 px-1.5 py-1.5 hover:bg-white/20 transition-colors cursor-pointer",
          className,
        )}
      >
        {/* Clean layering: first avatar on top, the rest (and the +N chip) tuck underneath. */}
        <span className="flex">
          {shown.map((v, i) => (
            <span
              key={v.id}
              className="relative -ml-2 first:ml-0"
              style={{ zIndex: shown.length - i }}
            >
              <StackAvatar viewer={v} />
            </span>
          ))}
          {overflow > 0 && (
            <span className="relative -ml-2" style={{ zIndex: 0 }}>
              <span className="size-7 rounded-full bg-white/15 text-white/90 ring-2 ring-white/15 flex items-center justify-center text-[10px] font-bold tabular-nums select-none">
                {overflowLabel}
              </span>
            </span>
          )}
        </span>
      </button>

      {!onTap && open && (
        <AudienceSheet
          presence={presence}
          onClose={() => setOpen(false)}
          zClassName={sheetZClassName}
        />
      )}
    </>
  );
}

export const PresenceFacepile = memo(PresenceFacepileImpl);
